<!--
  Engineer work-permit history row: dual column with "show external to tenant/contractor"
  disclosure control, aligned internal/external labels and bodies (no separate chrome column).
-->
<template>
  <div
    class="workpermit-content-slot__history-split-body workpermit-content-slot__history-split-body--disclosure-grid"
    :class="{ 'workpermit-content-slot__history-split-body--disclosure-grid-indicator': disclosureDisabled }"
  >
    <div class="workpermit-content-slot__history-disclosure-grid-bg-col2" aria-hidden="true" />
    <div class="workpermit-content-slot__history-disclosure-grid-head-left">
      <WpHistoryCommentHeading
        :index="item.index"
        :title="item.title"
        :byline="item.by"
      />
    </div>
    <div class="workpermit-content-slot__history-disclosure-grid-head-right">
      <label
        class="workpermit-content-slot__show-external-to-contractor"
        :class="{ 'workpermit-content-slot__show-external-to-contractor--read-only': disclosureDisabled }"
      >
        <input
          :checked="disclosureChecked"
          :disabled="disclosureDisabled"
          type="checkbox"
          class="general-checkbox"
          @change="$emit('disclosure-change', $event.target.checked)"
        >
        <span class="workpermit-content-slot__show-external-to-contractor-text">{{ $t('workPermit.showExternalCommentForContractor') }}</span>
      </label>
    </div>
    <div class="workpermit-content-slot__history-disclosure-grid-label-left">
      <span class="workpermit-content-slot__history-label">{{ $t('workPermit.internalCommentsHistoryLabel') }}</span>
    </div>
    <div class="workpermit-content-slot__history-disclosure-grid-label-right">
      <span class="workpermit-content-slot__history-label">{{ $t('workPermit.externalCommentsHistoryLabel') }}</span>
    </div>
    <div class="workpermit-content-slot__history-disclosure-grid-body-left wp-history-internal-comment">
      <pre class="workpermit-content-slot__history-pre">{{ internalBody }}</pre>
    </div>
    <div class="workpermit-content-slot__history-disclosure-grid-body-right wp-history-external-comment">
      <pre class="workpermit-content-slot__history-pre">{{ externalBody }}</pre>
    </div>
  </div>
</template>

<script>
import WpHistoryCommentHeading from './WpHistoryCommentHeading'

export default {
  name: 'WpHistoryDisclosureGrid',
  components: {
    WpHistoryCommentHeading,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    internalBody: {
      default: null,
    },
    externalBody: {
      default: null,
    },
    disclosureChecked: {
      type: Boolean,
      required: true,
    },
    /** When true, checkbox reflects persisted disclosure only (internal indicator). */
    disclosureDisabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.workpermit-content-slot__history-split-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 24px;
}

.workpermit-content-slot__history-split-body--disclosure-grid {
  position: relative;
  align-items: stretch;
  grid-template-rows: auto auto minmax(0, auto);

  .workpermit-content-slot__history-disclosure-grid-bg-col2 {
    grid-column: 2;
    grid-row: 1 / -1;
    z-index: 0;
    pointer-events: none;
    margin: 0;
    min-width: 0;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .workpermit-content-slot__history-disclosure-grid-head-left {
    grid-column: 1;
    grid-row: 1;
    z-index: 1;
    position: relative;
    min-width: 0;
    align-self: start;
    box-sizing: border-box;
  }

  .workpermit-content-slot__history-disclosure-grid-head-right {
    grid-column: 2;
    grid-row: 1;
    z-index: 1;
    position: relative;
    min-width: 0;
    align-self: start;
    padding: 3px 14px 0;
    box-sizing: border-box;
  }

  .workpermit-content-slot__history-disclosure-grid-label-left {
    grid-column: 1;
    grid-row: 2;
    z-index: 1;
    position: relative;
    align-self: start;
    min-width: 0;
    box-sizing: border-box;
  }

  .workpermit-content-slot__history-disclosure-grid-label-right {
    grid-column: 2;
    grid-row: 2;
    z-index: 1;
    position: relative;
    align-self: start;
    min-width: 0;
    padding: 0 14px;
    box-sizing: border-box;
  }

  .workpermit-content-slot__history-disclosure-grid-body-left {
    grid-column: 1;
    grid-row: 3;
    z-index: 1;
    position: relative;
    min-width: 0;
    align-self: stretch;
    box-sizing: border-box;
  }

  .workpermit-content-slot__history-disclosure-grid-body-right {
    grid-column: 2;
    grid-row: 3;
    z-index: 1;
    position: relative;
    min-width: 0;
    align-self: stretch;
    padding: 0 14px 12px;
    box-sizing: border-box;
  }

  /* Read-only disclosure indicator: no chrome card behind external column (approver-editable stage keeps default). */
  &.workpermit-content-slot__history-split-body--disclosure-grid-indicator {
    .workpermit-content-slot__history-disclosure-grid-bg-col2 {
      background: #ffffff00;
      box-shadow: none;
    }
  }
}

.workpermit-content-slot__show-external-to-contractor {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  .general-checkbox {
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.workpermit-content-slot__show-external-to-contractor-text {
  min-width: 0;
  flex: 1 1 auto;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.workpermit-content-slot__show-external-to-contractor--read-only {
  cursor: default;
  font-weight: normal;
}

@media (max-width: 640px) {
  .workpermit-content-slot__history-split-body {
    grid-template-columns: minmax(0, 1fr);
  }

  .workpermit-content-slot__history-split-body--disclosure-grid {
    .workpermit-content-slot__history-disclosure-grid-bg-col2 {
      display: none;
    }

    .workpermit-content-slot__history-disclosure-grid-head-left {
      grid-column: 1;
      grid-row: 1;
    }

    .workpermit-content-slot__history-disclosure-grid-label-left {
      grid-column: 1;
      grid-row: 2;
    }

    .workpermit-content-slot__history-disclosure-grid-body-left {
      grid-column: 1;
      grid-row: 3;
    }

    .workpermit-content-slot__history-disclosure-grid-head-right {
      grid-column: 1;
      grid-row: 4;
      padding: 3px 14px 0;
      background: #fff;
      border-radius: 4px 4px 0 0;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    }

    .workpermit-content-slot__history-disclosure-grid-label-right {
      grid-column: 1;
      grid-row: 5;
      padding: 0 14px;
      background: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    }

    .workpermit-content-slot__history-disclosure-grid-body-right {
      grid-column: 1;
      grid-row: 6;
      padding: 0 14px 12px;
      background: #fff;
      border-radius: 0 0 4px 4px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
      margin-bottom: 2px;
    }

    &.workpermit-content-slot__history-split-body--disclosure-grid-indicator {
      .workpermit-content-slot__history-disclosure-grid-head-right,
      .workpermit-content-slot__history-disclosure-grid-label-right,
      .workpermit-content-slot__history-disclosure-grid-body-right {
        background: #ffffff00;
        box-shadow: none;
        border-radius: 0;
      }
    }
  }
}
</style>
